import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useFormikContext, getIn } from 'formik';
import { Grid } from '@material-ui/core';
import { lookupsJson, StructureCategory } from '@ourbranch/lookups';

import {
  getDwellingHeatingOptionsForFO6,
  getFO6ConstructionTypeOptions,
  getMaskedFO6StructureCategoryOptions,
  getMaskedOccupancyUseTypeOptions,
  calculateGrainBinHeight
} from 'common/helpers/farm-helpers';
import { usesLengthWidth } from '@ourbranch/farm-utils';
import Field from 'core/components/form/form.v2';
import { Label } from 'core/components/label';
import { useStore } from 'core/store';
import { LabelTooltip } from 'core/components/label-tooltip';
import { propDetailsCodes } from '../quote-forms/quote-forms.constants';
import useStyles from './initial-risk-detail.styles';

// Form options constants
const bqOptions = lookupsJson.farm.buildingQuality;
const barnRtOptions = lookupsJson.farm.barnRoofType;
const buildingClassOptions = lookupsJson.farm.buildingClassOptions;

const FO6RiskDetail = () => {
  const classes = useStyles();
  const {
    quote: { getAlerts }
  } = useStore();

  const { values, isSubmitting, setFieldValue, errors } = useFormikContext();
  const alerts = getAlerts(propDetailsCodes, values.policyType);
  const has5004Alert = alerts.some((alert) => alert.code === 5004);
  const initialRiskDetailErrors = useRef(null);
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);

  // Extract frequently accessed nested values to improve readability
  const farmLocation = values.farmLocations?.[0];
  const farmRisk = farmLocation?.risks?.[0];
  const farmLocationPath = 'farmLocations[0]';
  const farmRiskPath = `${farmLocationPath}.risks[0]`;
  const buildingPath = `${farmRiskPath}.building`;

  const toggleAdditionalFields = useCallback(() => {
    setShowAdditionalFields(!showAdditionalFields);
  }, [showAdditionalFields]);

  useEffect(() => {
    if (alerts.length && initialRiskDetailErrors.current && isSubmitting) {
      initialRiskDetailErrors.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }, [alerts, isSubmitting]);

  useEffect(() => {
    if (has5004Alert) {
      setShowAdditionalFields(true);
    } else {
      setShowAdditionalFields(false);
    }
  }, [has5004Alert]);

  // Structure categories depend on state and policy type mask (#31367). Forreston Rental (Y) and Home (Z)
  // restrict FO-6 categories to Barns/Outbuildings and Fencing only; full Farm (F) shows all.
  // Resolve from the primary LOCATION's state, not the applicant/mailing state — an out-of-state
  // mailing address otherwise finds no mask and the category list comes back wrong (#32094).
  const fo6MaskState = farmLocation?.address?.state || values.state;
  const structureCategories = getMaskedFO6StructureCategoryOptions(values.policyType, fo6MaskState);
  const ctOptions = getFO6ConstructionTypeOptions(fo6MaskState);
  // "Occupancy or use type" options for the selected structure category. Home (Z) / Rental (Y)
  // restrict Barns/Outbuildings to a single "General Use" option; full Farm (F) shows all (#32485).
  const occupancyUseTypeOptions = getMaskedOccupancyUseTypeOptions(
    values.policyType,
    fo6MaskState,
    farmRisk?.building?.buildingType
  );

  // Check if structure category is Barns/Outbuildings, Storage Grain, Poultry Building, or new structure types
  const isBarnOutbuilding = farmRisk?.building?.buildingType === StructureCategory.BarnsOutbuildings;
  const isStorageGrain = farmRisk?.building?.buildingType === StructureCategory.SilosGrainBins;
  const isPoultryBuildings = farmRisk?.building?.buildingType === StructureCategory.PoultryBuildings;
  const isHoopBuilding = farmRisk?.building?.buildingType === StructureCategory.HoopBuildings;
  const isConfinementBuilding = farmRisk?.building?.buildingType === StructureCategory.ConfinementBuildings;
  const isFencing = farmRisk?.building?.buildingType === StructureCategory.Fencing;
  const isDwellingContents = farmRisk?.building?.buildingType === StructureCategory.DwellingContents;

  // Auto-set proper defaults for structure-specific fields
  useEffect(() => {
    if (isHoopBuilding) {
      // Hoop Building: Set roof type to indicate included in construction
      // Note: Hoop buildings automatically get 'HP' occupancy use in backend processing
      if (!farmRisk?.building?.roofType) {
        setFieldValue(`${buildingPath}.roofType`, 'NONEH');
      }
    }
  }, [isHoopBuilding, farmRisk?.building?.roofType, setFieldValue, buildingPath]);

  // Barns/Outbuildings, Hoop, Confinement, and Poultry Buildings: Default primary heating to 'NONE'
  // (GitHub issues #29270, #29351, #29398)
  useEffect(() => {
    const needsHeatingDefault = isBarnOutbuilding || isHoopBuilding || isConfinementBuilding || isPoultryBuildings;
    if (needsHeatingDefault && !farmRisk?.building?.primaryHeatingSource) {
      setFieldValue(`${buildingPath}.primaryHeatingSource`, 'NONE');
    }
  }, [
    isBarnOutbuilding,
    isHoopBuilding,
    isConfinementBuilding,
    isPoultryBuildings,
    farmRisk?.building?.primaryHeatingSource,
    setFieldValue,
    buildingPath
  ]);

  // Use normal dwelling heating options for underwriting (FO-1/FO-2/FO-3 style)
  // These will be mapped to agricultural codes when sending to 360Value/Verisk
  const currentHeatingOptions = getDwellingHeatingOptionsForFO6();

  // Check field requirements based on occupancy type
  const isTowerOccupancy = farmRisk?.building?.occupancy === 'GHT';
  const isBulkBinHopper = farmRisk?.building?.occupancy === 'GHH';
  const isGrainBin = farmRisk?.building?.occupancy === 'GHB';
  const isGrainLeg = farmRisk?.building?.occupancy === 'GL';
  const isLengthWidth = usesLengthWidth(farmRisk?.building?.occupancy);
  const shouldEnableDiameterHeight = isTowerOccupancy || isBulkBinHopper || isGrainBin;
  const shouldEnableSqFt = farmRisk?.building?.occupancy && !isTowerOccupancy;

  // Grain Bin: auto-calculate Height from Diameter + Bushel Capacity
  useEffect(() => {
    if (isGrainBin && farmRisk?.building?.diameter && farmRisk?.building?.bushelCapacity) {
      const calculatedHeight = calculateGrainBinHeight(farmRisk.building.diameter, farmRisk.building.bushelCapacity);
      setFieldValue(`${buildingPath}.height`, calculatedHeight);
    }
  }, [isGrainBin, farmRisk?.building?.diameter, farmRisk?.building?.bushelCapacity, buildingPath, setFieldValue]);

  // CC/GS/GH: auto-calculate sqFt from Length × Width
  useEffect(() => {
    if (isLengthWidth && farmRisk?.building?.length && farmRisk?.building?.width) {
      setFieldValue(`${buildingPath}.sqFt`, farmRisk.building.length * farmRisk.building.width);
    } else if (!isLengthWidth) {
      setFieldValue(`${buildingPath}.sqFt`, null);
    }
  }, [isLengthWidth, farmRisk?.building?.length, farmRisk?.building?.width, buildingPath, setFieldValue]);

  // Storage Grain: default Quality to Standard ('S')
  useEffect(() => {
    if (isStorageGrain && farmRisk?.building?.buildingQuality !== 'S') {
      setFieldValue(`${buildingPath}.buildingQuality`, 'S');
    }
  }, [isStorageGrain, farmRisk?.building?.buildingQuality, buildingPath, setFieldValue]);

  return (
    <Grid container spacing={2}>
      {/* Gate on the same resolved state used for the options (#32094) — using the mailing state
          here would suppress the dropdown when the location address (IL) is entered first. */}
      {fo6MaskState && (
        <Field
          xs={12}
          type="select"
          id={`${buildingPath}.buildingType`}
          name={`${buildingPath}.buildingType`}
          mode="light"
          label="Structure Category*"
          options={structureCategories}
          onChange={(val) => {
            if (['PB', 'PI', 'PO'].includes(val)) {
              setFieldValue(`${buildingPath}.occupancy`, 'PCH');
              setFieldValue(`${buildingPath}.primaryHeatingSource`, 'NONE');
            }
          }}
        />
      )}

      {isBarnOutbuilding && (
        <>
          {/* Row 1: Occupancy or use type + Year built in + Roof year */}
          <Field
            xs={4}
            type="select"
            id={`${buildingPath}.occupancy`}
            name={`${buildingPath}.occupancy`}
            mode="light"
            label="Occupancy or use type*"
            options={occupancyUseTypeOptions}
          />
          <Field
            xs={4}
            type="numeric"
            id={`${buildingPath}.yearBuilt`}
            name={`${buildingPath}.yearBuilt`}
            mode="light"
            label="Year built in*"
            format={{
              allowNegative: false
            }}
          />
          <Field
            xs={4}
            type="numeric"
            id={`${buildingPath}.roofYear`}
            name={`${buildingPath}.roofYear`}
            mode="light"
            label="Roof year*"
            format={{
              allowNegative: false
            }}
          />
          {/* Row 2: Total sqft + Quality + Roof type + Construction type */}
          <Field
            xs={3}
            type="numeric"
            id={`${buildingPath}.sqFt`}
            name={`${buildingPath}.sqFt`}
            mode="light"
            label="Total sqft*"
          />
          <Field
            xs={3}
            type="select"
            id={`${buildingPath}.buildingQuality`}
            name={`${buildingPath}.buildingQuality`}
            mode="light"
            label="Quality*"
            options={bqOptions}
          />
          <Field
            xs={3}
            type="select"
            id={`${buildingPath}.roofType`}
            name={`${buildingPath}.roofType`}
            mode="light"
            label="Roof type*"
            options={barnRtOptions}
          />
          <Field
            xs={3}
            type="select"
            id={`${buildingPath}.constructionType`}
            name={`${buildingPath}.constructionType`}
            mode="light"
            label="Construction type*"
            options={ctOptions}
          />
          {/* Row 3: Has open sides? + Building class type + Number of solid fuel burning... */}
          <Field
            xs={3}
            type="checkbox"
            id={`${buildingPath}.structureOpenSides`}
            name={`${buildingPath}.structureOpenSides`}
            mode="light"
            label="Has open sides?"
            className={classes.openSidesCheckbox}
            helperText={farmRisk?.building?.buildingClass === 1 ? 'Building Class 1 cannot have open sides' : undefined}
          />
          <Field
            xs={3}
            type="select"
            id={`${buildingPath}.buildingClass`}
            name={`${buildingPath}.buildingClass`}
            mode="light"
            label="Building class type"
            options={buildingClassOptions}
            fast={false}
            error={!!getIn(errors, `${buildingPath}.buildingClass`)}
            helperText={getIn(errors, `${buildingPath}.buildingClass`) || undefined}
          />
          <LabelTooltip
            label="Number of solid fuel burning..."
            tooltip={{
              label: 'More Info',
              onHoverText: `Count appliances that burn fuel like gas, propane, oil, or wood. Common examples: furnace, boiler, water heater, fireplace, wood stove, gas oven, gas dryer, or backup generators. Electric-only appliances don't count.`
            }}
            mode="light"
          >
            <Field
              xs={6}
              type="numeric"
              id="farmRisk.building.numSolidFuelBurningAppliances"
              name={`${buildingPath}.numSolidFuelBurningAppliances`}
              mode="light"
              format={{
                allowNegative: false
              }}
            />
          </LabelTooltip>
          {/* Row 4: Primary heating + Heating units */}
          <Field
            xs={6}
            type="select"
            id={`${buildingPath}.primaryHeatingSource`}
            name={`${buildingPath}.primaryHeatingSource`}
            mode="light"
            fast={false}
            label="Primary heating"
            options={currentHeatingOptions}
            truncateSelectedValue
          />
        </>
      )}

      {isStorageGrain && (
        <>
          <Field
            xs={6}
            type="select"
            id={`${buildingPath}.occupancy`}
            name={`${buildingPath}.occupancy`}
            mode="light"
            label="Occupancy or use type*"
            options={occupancyUseTypeOptions}
          />
          <Field
            xs={6}
            type="numeric"
            id={`${buildingPath}.yearBuilt`}
            name={`${buildingPath}.yearBuilt`}
            mode="light"
            label="Year built in*"
            format={{
              allowNegative: false
            }}
          />
          {!isLengthWidth && !isGrainLeg && (
            <Field
              xs={4}
              type="numeric"
              id={`${buildingPath}.diameter`}
              name={`${buildingPath}.diameter`}
              mode="light"
              label="Diameter (ft)"
              disabled={!shouldEnableDiameterHeight}
            />
          )}
          {isLengthWidth && (
            <>
              <Field
                xs={4}
                type="numeric"
                id={`${buildingPath}.length`}
                name={`${buildingPath}.length`}
                mode="light"
                label="Length (ft)"
              />
              <Field
                xs={4}
                type="numeric"
                id={`${buildingPath}.width`}
                name={`${buildingPath}.width`}
                mode="light"
                label="Width (ft)"
              />
            </>
          )}
          {!isTowerOccupancy && !isLengthWidth && (
            <Field
              xs={4}
              type="numeric"
              id={`${buildingPath}.bushelCapacity`}
              name={`${buildingPath}.bushelCapacity`}
              mode="light"
              label="Bushel capacity"
            />
          )}
          {!isLengthWidth && !isBulkBinHopper && (
            <Field
              xs={4}
              type="numeric"
              id={`${buildingPath}.height`}
              name={`${buildingPath}.height`}
              mode="light"
              label="Height (ft)"
              disabled={isGrainBin}
            />
          )}
          {!isGrainBin && !isTowerOccupancy && !isBulkBinHopper && !isGrainLeg && (
            <Field
              xs={4}
              type="numeric"
              id={`${buildingPath}.sqFt`}
              name={`${buildingPath}.sqFt`}
              mode="light"
              label="Total sqft"
              disabled={isLengthWidth || !shouldEnableSqFt}
            />
          )}
          <Field
            xs={6}
            type="value"
            id={`${buildingPath}.buildingQuality`}
            name={`${buildingPath}.buildingQuality`}
            mode="light"
            label="Quality"
            value="Standard"
          />
          <Field
            xs={6}
            type="select"
            id={`${buildingPath}.constructionType`}
            name={`${buildingPath}.constructionType`}
            mode="light"
            label="Construction type*"
            options={ctOptions}
          />
        </>
      )}

      {isPoultryBuildings && (
        <>
          {/* Row 1: Year built + Roof year + Total sqft */}
          <Field
            xs={3}
            type="numeric"
            id={`${buildingPath}.yearBuilt`}
            name={`${buildingPath}.yearBuilt`}
            mode="light"
            label="Year built in*"
            format={{
              allowNegative: false
            }}
          />
          <Field
            xs={3}
            type="numeric"
            id={`${buildingPath}.roofYear`}
            name={`${buildingPath}.roofYear`}
            mode="light"
            label="Roof year"
            format={{
              allowNegative: false
            }}
          />
          <Field
            xs={4}
            type="numeric"
            id={`${buildingPath}.sqFt`}
            name={`${buildingPath}.sqFt`}
            mode="light"
            label="Total sqft"
          />
          <Grid item xs={2} />
          {/* Row 2: Quality + Roof type */}
          <Field
            xs={3}
            type="select"
            id={`${buildingPath}.buildingQuality`}
            name={`${buildingPath}.buildingQuality`}
            mode="light"
            label="Quality*"
            options={bqOptions}
          />
          <Field
            xs={3}
            type="select"
            id={`${buildingPath}.roofType`}
            name={`${buildingPath}.roofType`}
            mode="light"
            label="Roof type"
            options={barnRtOptions}
          />
          <Field
            xs={6}
            type="select"
            id={`${buildingPath}.constructionType`}
            name={`${buildingPath}.constructionType`}
            mode="light"
            label="Construction type*"
            options={ctOptions}
          />
          {/* Row 3: Number of solid fuel burning... + Primary heating + Heating units */}
          <LabelTooltip
            label="Solid Fuel Burning Appliances"
            tooltip={{
              label: 'More Info',
              onHoverText: `Count appliances that burn solid fuels like wood, coal, or pellets. Common examples: fireplace, wood stove, pellet stove, wood-burning furnace, or coal stove. Gas, propane, oil, and electric appliances don't count.`
            }}
            mode="light"
          >
            <Field
              xs={4}
              type="numeric"
              id={`${buildingPath}.numSolidFuelBurningAppliances`}
              name={`${buildingPath}.numSolidFuelBurningAppliances`}
              mode="light"
              format={{
                allowNegative: false
              }}
            />
          </LabelTooltip>
          <Field
            xs={4}
            type="select"
            id={`${buildingPath}.primaryHeatingSource`}
            name={`${buildingPath}.primaryHeatingSource`}
            mode="light"
            label="Primary heating"
            options={currentHeatingOptions}
            truncateSelectedValue
          />
        </>
      )}

      {isHoopBuilding && (
        <>
          <Field
            xs={6}
            type="numeric"
            id={`${buildingPath}.yearBuilt`}
            name={`${buildingPath}.yearBuilt`}
            mode="light"
            label="Year built in*"
            format={{
              allowNegative: false
            }}
          />
          <Field
            xs={6}
            type="numeric"
            id={`${buildingPath}.roofYear`}
            name={`${buildingPath}.roofYear`}
            mode="light"
            label="Roof year"
            format={{
              allowNegative: false
            }}
          />
          <Field
            xs={6}
            type="numeric"
            id={`${buildingPath}.sqFt`}
            name={`${buildingPath}.sqFt`}
            mode="light"
            label="Total sqft"
          />
          <Field
            xs={6}
            type="select"
            id={`${buildingPath}.buildingQuality`}
            name={`${buildingPath}.buildingQuality`}
            mode="light"
            label="Quality*"
            options={bqOptions}
          />
          <Field
            xs={6}
            type="select"
            id={`${buildingPath}.roofType`}
            name={`${buildingPath}.roofType`}
            mode="light"
            label="Roof type"
            options={[{ id: 'HOOP', value: 'Hoop' }]}
            disabled={true}
            helperText="Roof type included in Hoop Building construction"
          />
          <Field
            xs={6}
            type="select"
            id={`${buildingPath}.constructionType`}
            name={`${buildingPath}.constructionType`}
            mode="light"
            label="Construction type*"
            options={ctOptions}
          />
          {/* Hoop Building Type field removed - occupancy is now automatically set to 'HP' */}
          <LabelTooltip
            label="Number of solid fuel burning..."
            tooltip={{
              label: 'More Info',
              onHoverText: `Count appliances that burn fuel like gas, propane, oil, or wood. Common examples: furnace, boiler, water heater, fireplace, wood stove, gas oven, gas dryer, or backup generators. Electric-only appliances don't count.`
            }}
            mode="light"
          >
            <Field
              xs={6}
              type="numeric"
              id={`${buildingPath}.numSolidFuelBurningAppliances`}
              name={`${buildingPath}.numSolidFuelBurningAppliances`}
              mode="light"
              format={{
                allowNegative: false
              }}
            />
          </LabelTooltip>
          <Field
            xs={6}
            type="select"
            id={`${buildingPath}.primaryHeatingSource`}
            name={`${buildingPath}.primaryHeatingSource`}
            mode="light"
            label="Primary heating"
            options={currentHeatingOptions}
            truncateSelectedValue
          />
        </>
      )}

      {isConfinementBuilding && (
        <>
          {/* Row 1: Occupancy or use type + Year built in + Roof year */}
          <Field
            xs={4}
            type="select"
            id={`${buildingPath}.occupancy`}
            name={`${buildingPath}.occupancy`}
            mode="light"
            label="Occupancy or use type*"
            options={occupancyUseTypeOptions}
          />
          <Field
            xs={4}
            type="numeric"
            id={`${buildingPath}.yearBuilt`}
            name={`${buildingPath}.yearBuilt`}
            mode="light"
            label="Year built in*"
            format={{
              allowNegative: false
            }}
          />
          <Field
            xs={4}
            type="numeric"
            id={`${buildingPath}.roofYear`}
            name={`${buildingPath}.roofYear`}
            mode="light"
            label="Roof year"
            format={{
              allowNegative: false
            }}
          />
          {/* Row 2: Total sqft + Quality + Roof type */}
          <Field
            xs={4}
            type="numeric"
            id={`${buildingPath}.sqFt`}
            name={`${buildingPath}.sqFt`}
            mode="light"
            label="Total sqft"
          />
          <Field
            xs={4}
            type="select"
            id={`${buildingPath}.buildingQuality`}
            name={`${buildingPath}.buildingQuality`}
            mode="light"
            label="Quality*"
            options={bqOptions}
          />
          <Field
            xs={4}
            type="select"
            id={`${buildingPath}.roofType`}
            name={`${buildingPath}.roofType`}
            mode="light"
            label="Roof type"
            options={barnRtOptions}
          />
          {/* Row 3: Construction type + Has open sides? */}
          <Field
            xs={4}
            type="select"
            id={`${buildingPath}.constructionType`}
            name={`${buildingPath}.constructionType`}
            mode="light"
            label="Construction type*"
            options={ctOptions}
          />
          <Grid item xs={4} />
          {/* Row 4: Number of solid fuel burning... + Primary heating + Heating units */}
          <LabelTooltip
            label="Number of solid fuel burning..."
            tooltip={{
              label: 'More Info',
              onHoverText: `Count appliances that burn fuel like gas, propane, oil, or wood. Common examples: furnace, boiler, water heater, fireplace, wood stove, gas oven, gas dryer, or backup generators. Electric-only appliances don't count.`
            }}
            mode="light"
          >
            <Field
              xs={4}
              type="numeric"
              id={`${buildingPath}.numSolidFuelBurningAppliances`}
              name={`${buildingPath}.numSolidFuelBurningAppliances`}
              mode="light"
              format={{
                allowNegative: false
              }}
            />
          </LabelTooltip>
          <Field
            xs={4}
            type="select"
            id={`${buildingPath}.primaryHeatingSource`}
            name={`${buildingPath}.primaryHeatingSource`}
            mode="light"
            label="Primary heating"
            options={currentHeatingOptions}
            truncateSelectedValue
          />
        </>
      )}

      {isFencing && (
        <>
          <Field
            xs={6}
            type="numeric"
            id={`${buildingPath}.yearBuilt`}
            name={`${buildingPath}.yearBuilt`}
            mode="light"
            label="Year built in*"
            format={{
              allowNegative: false
            }}
          />
          <Field
            xs={6}
            type="numeric"
            id={`${farmRiskPath}.coverages.coverageE`}
            name={`${farmRiskPath}.coverages.coverageE`}
            mode="light"
            label="Coverage Limit"
            format={{
              allowNegative: false,
              decimalScale: 0,
              thousandSeparator: true,
              prefix: '$'
            }}
          />
        </>
      )}

      {isDwellingContents && (
        <>
          {/* Row 1: Residence Type + Year Built + Roof Year */}
          <Field
            xs={4}
            type="select"
            id={`${buildingPath}.residenceType`}
            name={`${buildingPath}.residenceType`}
            mode="light"
            label="Residence type/usage"
            options={[
              { id: 'rental', value: 'Rental' },
              { id: 'seasonal', value: 'Seasonal' },
              { id: 'vacant', value: 'Vacant' }
            ]}
          />
          <Field
            xs={4}
            type="numeric"
            id={`${buildingPath}.yearBuilt`}
            name={`${buildingPath}.yearBuilt`}
            mode="light"
            label="Year built in*"
            format={{
              allowNegative: false
            }}
          />
          <Field
            xs={4}
            type="numeric"
            id={`${buildingPath}.roofYear`}
            name={`${buildingPath}.roofYear`}
            mode="light"
            label="Roof year"
            format={{
              allowNegative: false
            }}
          />
          {/* Row 2: Square Footage + Quality + Roof type + Construction type */}
          <Field
            xs={3}
            type="numeric"
            id={`${buildingPath}.sqFt`}
            name={`${buildingPath}.sqFt`}
            mode="light"
            label="Total sqft*"
          />
          <Field
            xs={3}
            type="select"
            id={`${buildingPath}.buildingQuality`}
            name={`${buildingPath}.buildingQuality`}
            mode="light"
            label="Quality*"
            options={bqOptions}
          />
          <Field
            xs={3}
            type="select"
            id={`${buildingPath}.roofType`}
            name={`${buildingPath}.roofType`}
            mode="light"
            label="Roof type*"
            options={barnRtOptions}
          />
          <Field
            xs={3}
            type="select"
            id={`${buildingPath}.constructionType`}
            name={`${buildingPath}.constructionType`}
            mode="light"
            label="Construction type*"
            options={ctOptions}
          />
          {/* Row 3: Solid Fuel Burning Appliances */}
          <LabelTooltip
            label="Solid Fuel Burning Appliances"
            tooltip={{
              label: 'More Info',
              onHoverText: `Count appliances that burn solid fuels like wood, coal, or pellets. Common examples: fireplace, wood stove, pellet stove, wood-burning furnace, or coal stove. Gas, propane, oil, and electric appliances don't count.`
            }}
            mode="light"
          >
            <Field
              xs={6}
              type="numeric"
              id={`${buildingPath}.numSolidFuelBurningAppliances`}
              name={`${buildingPath}.numSolidFuelBurningAppliances`}
              mode="light"
              format={{
                allowNegative: false
              }}
            />
          </LabelTooltip>
          {/* Row 4: Primary Heating */}
          <Field
            xs={6}
            type="select"
            id={`${buildingPath}.primaryHeatingSource`}
            name={`${buildingPath}.primaryHeatingSource`}
            mode="light"
            label="Primary heating"
            options={currentHeatingOptions}
            truncateSelectedValue
          />
          {/* Coverage E - Dwelling Limit */}
          <Field
            xs={6}
            type="numeric"
            id={`${farmRiskPath}.coverages.coverageE`}
            name={`${farmRiskPath}.coverages.coverageE`}
            mode="light"
            label="Coverage E - Dwelling Limit"
            format={{
              allowNegative: false,
              decimalScale: 0,
              thousandSeparator: true,
              prefix: '$'
            }}
          />
        </>
      )}

      {/* Risk address automatically inherits from location */}

      {has5004Alert && (
        <Grid item xs={12} className={classes.checkboxContainer}>
          <Label type="action" onClick={toggleAdditionalFields}>
            {showAdditionalFields ? 'Hide additional fields' : 'Show additional fields'}
          </Label>
        </Grid>
      )}
    </Grid>
  );
};

export default FO6RiskDetail;
